import { useState } from "react";
import { useNavigate } from "react-router";
import "tldraw/tldraw.css";
import useLiveSessionFirestore from "../../../hooks/useLiveSessionFirestore";
import { useAppSelector } from "../../../store/hooks";
import SpinnerScreen from "../../SpinnerScreen";
import CountdownTimer from "./CountdownTimer";
import FullscreenBtn from "./FullscreenBtn";
import useGetLiveSession from "./hooks/useGetLiveSession";
import useLessonStartStatus from "./hooks/useLessonStartStatus";
import useStreamIO from "./hooks/useStreamIO";
import LiveSessionTimer from "./LiveSessionTimer";
import SendQuestionTab from "./SendQuestionTab";
import SessionCanvas from "./SessionCanvas";
import Toolbar from "./Toolbar";
import styles from "./TutorLiveScreen.module.css";
import TutorSidebarTabs from "./TutorSidebarTabs";

const TutorLiveScreen = () => {
  const [isSendingQuestion, setIsSendingQuestion] = useState(false);
  const sessionData = useGetLiveSession();
  const { isStarted, loading: startStatusLoading } = useLessonStartStatus(
    sessionData?.lesson_id
  );
  const navigate = useNavigate();

  const { uid } = useAppSelector((state) => state.store.user);

  const {
    isMuted,
    isMicLoading,
    isRTCStarted,
    joinCall,
    leaveCall,
    setMicMuted,
    endCall,
  } = useStreamIO({
    uid: uid?.toString(),
    token: sessionData?.join_token,
    callId: sessionData?.lesson_id.toString(),
  });

  const {
    revokeSpeakingPermission,
    kickoutStudent,
    sendMessage,
    allowToSpeak,
    updateQuestion,
  } = useLiveSessionFirestore(sessionData?.lesson_id.toString());

  if (!sessionData || startStatusLoading) return <SpinnerScreen />;

  if (!isStarted)
    return (
      <CountdownTimer
        targetDate={sessionData?.session_start_date}
        sessionId={sessionData?.lesson_id}
      />
    );

  return (
    <div className="fade-in m-0 p-0 w-full h-screen bg-black flex flex-col grow justify-center items-center">
      <div id="tldraw-live-session" className={styles.container}>
        <div className={styles.header}>
          <FullscreenBtn />
          <span className={styles.title}>{sessionData.title}</span>
          <LiveSessionTimer
            startDate={sessionData.started_at}
            sessionId={sessionData?.lesson_id}
            sessionEnded={sessionData?.is_ended}
            sessionDuration={sessionData?.session_duration}
            endCall={endCall}
          />
          <button
            className={styles.closeBtn}
            onClick={async () => {
              await leaveCall();
              navigate(-1);
            }}
          >
            <img
              src="https://eliteacademymedia.s3.us-east-1.amazonaws.com/qashqool_assets/media/exit-icon.svg"
              alt="انهاء الحصة"
            />
          </button>
        </div>
        <div className={styles.body}>
          <div className={styles.sidebar}>
            <Toolbar
              isStarted={isStarted}
              isMuted={isMuted}
              isMicLoading={isMicLoading}
              isRTCStarted={isRTCStarted}
              isSendingQuestion={isSendingQuestion}
              joinCall={joinCall}
              leaveCall={leaveCall}
              setMicMuted={setMicMuted}
              setIsSendingQuestion={setIsSendingQuestion}
            />
            {isSendingQuestion ? (
              <div className={styles.sendQuestionTab}>
                <SendQuestionTab
                  updateQuestion={updateQuestion}
                  setIsSendingQuestion={setIsSendingQuestion}
                />
              </div>
            ) : (
              <TutorSidebarTabs
                revokeSpeakingPermission={revokeSpeakingPermission}
                kickoutStudent={kickoutStudent}
                sendMessage={sendMessage}
                allowToSpeak={allowToSpeak}
              />
            )}
          </div>
          <div className={styles.sessionContent}>
            <SessionCanvas sessionId={sessionData?.lesson_id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorLiveScreen;
